import { alignLoopbackBrowserOrigin } from "@/lib/loopback-origin";

type PublicEnvironmentInput = {
  NEXT_PUBLIC_API_URL?: string;
  NEXT_PUBLIC_TIKTOK_INTEGRATION_URL?: string;
};

function required(
  environment: PublicEnvironmentInput,
  key: keyof PublicEnvironmentInput,
) {
  const value = environment[key]?.trim();
  if (!value) {
    throw new Error(`缺少环境变量 ${key}`);
  }
  return value;
}

function httpUrl(environment: PublicEnvironmentInput, key: keyof PublicEnvironmentInput) {
  const value = required(environment, key);

  try {
    const url = new URL(value);
    if (!["http:", "https:"].includes(url.protocol)) {
      throw new Error("invalid protocol");
    }
    if (
      url.username ||
      url.password ||
      url.pathname !== "/" ||
      url.search ||
      url.hash
    ) {
      throw new Error("invalid origin");
    }
    return url.origin;
  } catch {
    throw new Error(`${key} 必须是精确的 HTTP(S) origin`);
  }
}

/** 静态 Web 只公开浏览器访问 FC API 所需的 HTTP(S) URL。 */
export function readPublicEnvironment(environment: PublicEnvironmentInput) {
  return {
    apiUrl: httpUrl(environment, "NEXT_PUBLIC_API_URL"),
    tiktokIntegrationUrl: httpUrl(environment, "NEXT_PUBLIC_TIKTOK_INTEGRATION_URL"),
  };
}

function currentPublicEnvironment() {
  return readPublicEnvironment({
    NEXT_PUBLIC_API_URL: import.meta.env.NEXT_PUBLIC_API_URL,
    NEXT_PUBLIC_TIKTOK_INTEGRATION_URL:
      import.meta.env.NEXT_PUBLIC_TIKTOK_INTEGRATION_URL,
  });
}

function browserPageOrigin() {
  if (typeof window === "undefined") return null;
  return window.location.origin;
}

/** 延迟读取 Vite 公开环境变量，便于构建与测试分别注入配置。 */
export const publicEnv = {
  get apiUrl() {
    return alignLoopbackBrowserOrigin(
      currentPublicEnvironment().apiUrl,
      browserPageOrigin(),
    );
  },
  get tiktokIntegrationUrl() {
    return alignLoopbackBrowserOrigin(
      currentPublicEnvironment().tiktokIntegrationUrl,
      browserPageOrigin(),
    );
  },
};
